import React, { useRef, useState } from 'react';

import classNames from 'classnames';
import { Check, ChevronDown, RotateCcw, Upload, X } from 'lucide-react';
import { DateTime } from 'luxon';
import { queryCache, useMutation } from 'react-query';
import { Toast } from 'src/common/components';

import {
  approveCandidate,
  patchCandidate,
  rejectCandidate,
  unrejectCandidate,
  uploadCandidateMedia,
} from '../api';
import {
  AUDIENCE_OPTIONS,
  CANDIDATES_QUERY_KEY,
  CATEGORY_COLOR,
  CATEGORY_LABEL,
  CATEGORY_OPTIONS,
  CTA_TYPE_OPTIONS,
  RENDER_SLOT_OPTIONS,
} from '../constants';
import { CandidateAudience, DigestEditionCandidate, FeatureCategory } from '../types';

import CandidateReadinessIssues from './CandidateReadinessIssues';
import { CandidateStatusBadge } from './EditionStatusBadge';

type ApiError = { response?: { data?: { detail?: string } } };

const errorMessage = (error: unknown, fallback: string): string =>
  (error as ApiError)?.response?.data?.detail || fallback;

const formatAuditDate = (iso: string | null): string => {
  const dt = iso ? DateTime.fromISO(iso) : null;
  return dt?.isValid ? dt.toLocaleString(DateTime.DATETIME_MED) : '—';
};

const getAuditNote = (c: DigestEditionCandidate): string | null => {
  const note = (action: string, userId: number | null, at: string | null): string | null =>
    userId ? `${action} por usuario #${userId} el ${formatAuditDate(at)}` : null;
  if (c.approval_status === 'approved') return note('Aprobado', c.approved_by, c.approved_at);
  if (c.approval_status === 'rejected') {
    return note('Quitado de la edición', c.approved_by, c.approved_at);
  }
  return note('Devuelto a pendiente', c.returned_to_pending_by, c.returned_to_pending_at);
};

type CandidateCardProps = {
  candidate: DigestEditionCandidate;
  editionId: string;
  onSaved: () => void;
};

const CandidateCard: React.FC<CandidateCardProps> = ({ candidate, editionId, onSaved }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fields, setFields] = useState({
    headline_override: candidate.headline_override,
    benefit_copy_override: candidate.benefit_copy_override,
    cta_label: candidate.cta_label,
    cta_url: candidate.cta_url,
    cta_type: candidate.cta_type,
    category: candidate.category,
    media_url: candidate.media_url,
    media_alt: candidate.media_alt,
    audience: candidate.audience,
    render_slot: candidate.render_slot,
    support_article_url: candidate.support_article_url ?? '',
  });
  const [isDirty, setIsDirty] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const invalidate = (): void => {
    void queryCache.invalidateQueries([CANDIDATES_QUERY_KEY, editionId]);
  };

  const validateFields = (): string[] => {
    const missing: string[] = [];
    if (!fields.headline_override.trim()) missing.push('Headline');
    if (!fields.benefit_copy_override.trim()) missing.push('Beneficio');
    if (!fields.cta_label.trim()) missing.push('CTA label');
    if (!fields.category) missing.push('Categoría');
    if (fields.cta_type === 'self_serve' && !fields.cta_url.trim()) missing.push('CTA URL');
    return missing;
  };

  const [save, { isLoading: isSaving }] = useMutation(() => patchCandidate(candidate.id, fields), {
    onSuccess: () => {
      setIsDirty(false);
      invalidate();
      onSaved();
      Toast.success('Candidato guardado.');
    },
    onError: () => {
      Toast.error('Error al guardar el candidato.');
    },
  });

  const handleSave = (): void => {
    const missing = validateFields();
    if (missing.length > 0) {
      Toast.error(`Campos requeridos: ${missing.join(', ')}`);
      return;
    }
    save();
  };

  const [approve, { isLoading: isApproving }] = useMutation(() => approveCandidate(candidate.id), {
    onSuccess: () => {
      invalidate();
      onSaved();
      Toast.success('Candidato aprobado.');
    },
    onError: () => {
      Toast.error('Error al aprobar.');
    },
  });

  const handleApprove = (): void => {
    const missing = validateFields();
    if (missing.length > 0) {
      Toast.error(`Completa estos campos antes de aprobar: ${missing.join(', ')}`);
      return;
    }
    approve();
  };

  const isApproved = candidate.approval_status === 'approved';
  const isRejected = candidate.approval_status === 'rejected';
  const isEditionSent = candidate.edition_status === 'sent';

  const [reject, { isLoading: isRejecting }] = useMutation(() => rejectCandidate(candidate.id), {
    onSuccess: () => {
      invalidate();
      onSaved();
      Toast.success('Candidato rechazado.');
    },
    onError: (error) => {
      Toast.error(errorMessage(error, 'Error al rechazar.'));
    },
  });

  const [unreject, { isLoading: isUnrejecting }] = useMutation(
    () => unrejectCandidate(candidate.id),
    {
      onSuccess: () => {
        invalidate();
        onSaved();
        Toast.success(isApproved ? 'Candidato devuelto a pendiente.' : 'Rechazo revertido.');
      },
      onError: (error) => {
        Toast.error(errorMessage(error, 'Error al revertir el rechazo.'));
      },
    }
  );

  const update = <K extends keyof typeof fields>(key: K, value: typeof fields[K]): void => {
    setFields((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    setIsUploading(true);
    try {
      const res = await uploadCandidateMedia(candidate.id, file);
      update('media_url', res.data.url);
      if (res.data.warning === 'local_storage') {
        Toast.warning('Imagen guardada localmente. No se verá en emails — usa una URL pública.');
      } else {
        Toast.success('Imagen subida.');
      }
    } catch {
      Toast.error('Error al subir la imagen.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const categoryColor = fields.category ? CATEGORY_COLOR[fields.category] : '#3D5D98';
  const isBusy = isSaving || isApproving || isRejecting || isUnrejecting || isUploading;
  const auditNote = getAuditNote(candidate);

  const borderColor = isApproved ? '#10B06C' : isExpanded ? categoryColor : '#E5E7EB';

  return (
    <div
      className={classNames(
        'border-2 rounded-2xl p-5 space-y-3 bg-white transition-colors',
        isRejected && 'opacity-60'
      )}
      style={{ borderColor }}
    >
      <button
        type="button"
        className="flex items-center w-full gap-2.5 text-left py-0.5"
        onClick={() => setIsExpanded((v) => !v)}
      >
        <span
          className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: categoryColor }}
        />
        <span className="flex-1 min-w-0 text-sm font-semibold text-gray-800 truncate">
          {fields.headline_override || <span className="italic text-gray-400">Sin título</span>}
        </span>
        {fields.category && (
          <span
            className="text-xs px-2.5 py-0.5 rounded-full font-semibold flex-shrink-0 border"
            style={{ color: categoryColor, borderColor: categoryColor }}
          >
            {CATEGORY_LABEL[fields.category]}
          </span>
        )}
        <CandidateStatusBadge status={candidate.approval_status} />
        {!isRejected && (
          <ChevronDown
            size={14}
            className={classNames(
              'flex-shrink-0 text-gray-400 transition-transform',
              isExpanded && 'rotate-180'
            )}
          />
        )}
      </button>

      {!isRejected && <CandidateReadinessIssues candidate={candidate} />}

      {isExpanded && (
        <div className="space-y-2">
          <label className="block">
            <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Headline
            </span>
            <input
              className="input mt-0.5 w-full text-sm"
              value={fields.headline_override}
              placeholder="Título del feature"
              onChange={(e) => update('headline_override', e.target.value)}
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Beneficio
            </span>
            <textarea
              className="input mt-0.5 w-full text-sm resize-none"
              rows={3}
              value={fields.benefit_copy_override}
              placeholder="¿Qué gana el cliente?"
              onChange={(e) => update('benefit_copy_override', e.target.value)}
            />
          </label>

          {/* Media upload */}
          <div>
            <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Media (imagen o GIF)
            </span>
            <div className="mt-0.5 space-y-1">
              <div className="flex gap-2">
                <input
                  className="input flex-1 text-sm"
                  type="url"
                  value={fields.media_url}
                  placeholder="https://… o sube un archivo"
                  onChange={(e) => update('media_url', e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-ghost flex items-center gap-1 px-2 py-1 text-sm"
                  disabled={isBusy}
                  title="Subir archivo"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload size={14} className={isUploading ? 'animate-bounce' : ''} />
                  {isUploading ? 'Subiendo…' : 'Subir'}
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,image/gif"
                className="hidden"
                onChange={(e) => void handleFileChange(e)}
              />
              {fields.media_url && (
                <>
                  <img
                    src={fields.media_url}
                    alt={fields.media_alt || 'preview'}
                    className="max-h-24 object-cover mt-1 border border-gray-200 rounded"
                    onError={({ currentTarget }) => {
                      currentTarget.style.display = 'none'; // eslint-disable-line no-param-reassign
                    }}
                  />
                  {/^https?:\/\/localhost/.test(fields.media_url) && (
                    <p className="text-amber-600 text-xs font-medium">
                      ⚠️ URL local — no se verá en emails. Usa una URL pública o sube el archivo
                      cuando S3 esté configurado.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {fields.media_url && (
            <label className="block">
              <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Media Alt
              </span>
              <input
                className="input mt-0.5 w-full text-sm"
                value={fields.media_alt}
                placeholder="Descripción de la imagen"
                onChange={(e) => update('media_alt', e.target.value)}
              />
            </label>
          )}

          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                CTA label
              </span>
              <input
                className="input mt-0.5 w-full text-sm"
                value={fields.cta_label}
                placeholder="Conoce más"
                onChange={(e) => update('cta_label', e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                CTA tipo
              </span>
              <select
                className="input mt-0.5 w-full text-sm"
                value={fields.cta_type}
                onChange={(e) => update('cta_type', e.target.value as typeof fields.cta_type)}
              >
                {CTA_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {fields.cta_type === 'self_serve' && (
            <label className="block">
              <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                CTA URL
              </span>
              <input
                className="input mt-0.5 w-full text-sm"
                type="url"
                value={fields.cta_url}
                placeholder="https://..."
                onChange={(e) => update('cta_url', e.target.value)}
              />
            </label>
          )}

          <label className="block">
            <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Artículo de ayuda (URL)
            </span>
            <input
              className="input mt-0.5 w-full text-sm"
              type="url"
              value={fields.support_article_url}
              placeholder="https://help.brainhi.com/..."
              onChange={(e) => update('support_article_url', e.target.value)}
            />
          </label>

          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Categoría
              </span>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span
                  className="flex-shrink-0 w-3 h-3 border border-gray-200 rounded-full"
                  style={{ backgroundColor: categoryColor }}
                />
                <select
                  className="input w-full text-sm"
                  value={fields.category ?? ''}
                  onChange={(e) => update('category', (e.target.value as FeatureCategory) || null)}
                >
                  <option value="">Sin categoría</option>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Slot
              </span>
              <select
                className="input mt-0.5 w-full text-sm"
                value={fields.render_slot}
                onChange={(e) => update('render_slot', e.target.value as typeof fields.render_slot)}
              >
                {RENDER_SLOT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Audiencia
            </span>
            <select
              className="input mt-0.5 w-full text-sm"
              value={fields.audience}
              onChange={(e) => update('audience', e.target.value as CandidateAudience)}
            >
              {AUDIENCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          {auditNote && <p className="text-xs text-gray-500">{auditNote}</p>}

          <div className="flex items-center justify-end gap-2 pt-1 border-t">
            {isRejected && !isEditionSent && (
              <button
                type="button"
                className="btn btn-ghost hover:bg-amber-50 text-amber-700 flex items-center gap-1 px-3 py-1 text-sm"
                disabled={isBusy}
                onClick={() => unreject()}
              >
                {isUnrejecting ? 'Revertiendo…' : 'Revertir rechazo'}
              </button>
            )}

            {isApproved && !isEditionSent && (
              <>
                <button
                  type="button"
                  className="btn btn-ghost hover:bg-red-50 flex items-center gap-1 px-3 py-1 text-sm text-red-600"
                  disabled={isBusy}
                  onClick={() => reject()}
                >
                  <X size={14} />
                  {isRejecting ? 'Quitando…' : 'Quitar de la edición'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost hover:bg-amber-50 text-amber-700 flex items-center gap-1 px-3 py-1 text-sm"
                  disabled={isBusy}
                  onClick={() => unreject()}
                >
                  <RotateCcw size={14} />
                  {isUnrejecting ? 'Devolviendo…' : 'Devolver a pendiente'}
                </button>
              </>
            )}

            {!isRejected && !isApproved && !isEditionSent && (
              <button
                type="button"
                className="btn btn-ghost hover:bg-red-50 flex items-center gap-1 px-3 py-1 text-sm text-red-600"
                disabled={isBusy}
                onClick={() => reject()}
              >
                <X size={14} />
                {isRejecting ? 'Rechazando…' : 'Rechazar'}
              </button>
            )}

            {!isApproved && !isRejected && !isEditionSent && (
              <button
                type="button"
                className="btn btn-ghost hover:bg-green-50 flex items-center gap-1 px-3 py-1 text-sm text-green-700"
                disabled={isBusy || isDirty}
                title={isDirty ? 'Guarda primero antes de aprobar' : undefined}
                onClick={handleApprove}
              >
                <Check size={14} />
                {isApproving ? 'Aprobando…' : 'Aprobar'}
              </button>
            )}

            {!isRejected && (
              <button
                type="button"
                className={classNames(
                  'btn text-sm py-1 px-3',
                  isDirty ? 'btn-secondary' : 'btn-ghost'
                )}
                disabled={!isDirty || isBusy}
                onClick={handleSave}
              >
                {isSaving ? 'Guardando…' : 'Guardar'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateCard;
